import { Component, Inject, OnInit, Optional, ViewChild, TemplateRef } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Compbaser } from "../compbaser";
import { IChartType } from '../../interfaces';
import * as _ from 'lodash';

@Component({
    selector: 'app-chart-editor',
    templateUrl: './chart-editor.component.html',
    styleUrls: ['./chart-editor.component.scss']
})
export class ChartEditorComponent extends Compbaser implements OnInit {
    // Import dialog properties
    @ViewChild('importDataDialog') importDataDialog!: TemplateRef<any>;

    chartData: Array<{ name: string; value: string[] }> = [];
    chartType: IChartType = 'column'
    legendData: { colors: Array<any>; titles: Array<string> } = { colors: [], titles: [] };

    // Table structure for expandable grid
    columns: Array<{ id: string; label: string; expanded: boolean }> = [
        { id: 'A', label: 'A', expanded: false }
    ];

    rows: Array<{ id: string; label: string; expanded: boolean; data: { [key: string]: string } }> = [];
    columnColors: Array<string> = [];

    importJsonText = '';
    jsonError = '';
    importDialogRef?: MatDialogRef<any>;
    enableOnlyFirstColumn = false;
    enableOnlyFirstRow = false;

    constructor(
        protected override dialog: MatDialog,
        @Optional() public dialogRef?: MatDialogRef<ChartEditorComponent>,
        @Optional() @Inject(MAT_DIALOG_DATA) public data?: any
    ) {
        super(dialog);
        if (dialogRef || data) {
            this.chartData = data?.chartData || [];
            this.chartType = data?.chartType || 'column';
            this.legendData = data?.legendData || { colors: [], titles: [] };
            // Single value charts (only use first column)
            this.enableOnlyFirstColumn = ['pie', 'doughnut', 'gauge', 'funnel', 'treemap', 'sunburst', 'sankey', 'graph', 'waterfall', 'rose', 'negativeBar', 'liquidFill'].includes(this.chartType);
            // Single row charts (only use first row)
            this.enableOnlyFirstRow = ['gauge'].includes(this.chartType);
            this.initializeFromChartData();
            this.initializeColumnColors();
        }
    }

    /**
     * Initialize component after Angular sets up data-bound properties
     */
    ngOnInit() {
    }

    /**
     * Convert chart data into table format with proper columns and rows
     */
    private initializeFromChartData() {
        if (this.chartData && this.chartData.length > 0) {
            // First, determine the maximum number of columns needed
            const maxColumns = Math.max(...this.chartData.map(item => item.value.length));
            
            // Initialize columns array based on the data
            this.columns = [];
            for (let i = 0; i < maxColumns; i++) {
                const columnId = String.fromCharCode(65 + i); // A, B, C, D, etc.
                const columnLabel = this.legendData.titles[i] || columnId; // Use legend title if available
                this.columns.push({
                    id: columnId,
                    label: columnLabel,
                    expanded: false
                });
            }
            
            // Convert chartData to table format
            this.rows = this.chartData.map((item, index) => ({
                id: `row_${index}`,
                label: item.name,
                expanded: false,
                data: item.value.reduce((acc, val, idx) => {
                    acc[this.columns[idx]?.id || `col_${idx}`] = val;
                    return acc;
                }, {} as { [key: string]: string })
            }));
        }
    }

    /**
     * Remove a column from the table
     */
    removeColumn(columnId: string) {
        // Find the column index before removing
        const columnIndex = this.columns.findIndex(col => col.id === columnId);
        
        if (columnIndex === -1) return; // Column not found
        
        // Remove the column from columns array
        this.columns = this.columns.filter(col => col.id !== columnId);
        
        // Remove the column data from all rows
        this.rows.forEach(row => {
            delete row.data[columnId];
        });
        
        // Remove the column color
        this.columnColors.splice(columnIndex, 1);
        
        // If no columns left, add a default one
        if (this.columns.length === 0) {
            this.addColumn();
        }
    }

    /**
     * Add a new column to the table
     */
    addColumn() {
        // Generate next available column ID based on existing columns
        const existingIds = this.columns.map(col => col.id);
        let newColumnId = 'A';
        let charCode = 65; // Start with 'A'
        
        while (existingIds.includes(newColumnId)) {
            charCode++;
            newColumnId = String.fromCharCode(charCode);
        }
        
        this.columns.push({
            id: newColumnId,
            label: newColumnId,
            expanded: false
        });
        
        // Add empty data for new column in all rows
        this.rows.forEach(row => {
            row.data[newColumnId] = '';
        });
        
        // Add default color for new column
        const defaultColors = ['#2196F3', '#FF5722', '#4CAF50', '#FF9800', '#9C27B0', '#607D8B'];
        const newColumnIndex = this.columns.length - 1;
        this.columnColors[newColumnIndex] = defaultColors[newColumnIndex % defaultColors.length];
    }

    /**
     * Remove a row from the table
     */
    removeRow(rowId: string) {
        this.rows = this.rows.filter(row => row.id !== rowId);
    }

    /**
     * Add a new row to the table
     */
    addRow() {
        const newRow = {
            id: `row_${this.rows.length}`,
            label: `New Row ${this.rows.length + 1}`,
            expanded: false,
            data: {} as { [key: string]: string }
        };
        
        // Initialize data for all columns
        this.columns.forEach(col => {
            newRow.data[col.id] = '';
        });
        
        this.rows.push(newRow);
    }

    /**
     * Update the value of a specific cell
     */
    updateCellValue(rowId: string, columnId: string, value: string) {
        const row = this.rows.find(r => r.id === rowId);
        if (row) {
            row.data[columnId] = value;
        }
    }

    /**
     * Update the label of a specific row
     */
    updateRowLabel(rowId: string, label: string) {
        const row = this.rows.find(r => r.id === rowId);
        if (row) {
            row.label = label;
        }
    }

    /**
     * Update the label of a specific column
     */
    updateColumnLabel(columnId: string, label: string) {
        const column = this.columns.find(col => col.id === columnId);
        if (column) {
            column.label = label;
        }
    }

    /**
     * Export table data back to chart data format
     */
    exportToChartData(): Array<{ name: string; value: string[] }> {
        return this.rows.map(row => ({
            name: row.label,
            value: this.columns.map(col => row.data[col.id] || '')
        }));
    }

    /**
     * Handle column label edit completion
     */
    onColumnLabelBlur(columnId: string, event: Event) {
        const value = (event.target as HTMLInputElement).value;
        this.updateColumnLabel(columnId, value || '');
    }

    /**
     * Handle row label edit completion
     */
    onRowLabelBlur(rowId: string, event: Event) {
        const value = (event.target as HTMLInputElement).value;
        this.updateRowLabel(rowId, value || '');
    }

    /**
     * Handle cell input blur event
     */
    onCellInputBlur(rowId: string, columnId: string, event: Event) {
        const value = (event.target as HTMLInputElement).value;
        this.updateCellValue(rowId, columnId, value);
    }

    /**
     * Handle cell input enter key press
     */
    onCellInputEnter(rowId: string, columnId: string, event: Event) {
        const value = (event.target as HTMLInputElement).value;
        this.updateCellValue(rowId, columnId, value);
    }

    /**
     * Initialize column colors from legend data or use defaults
     */
    initializeColumnColors() {
        // Initialize column colors from legendData or use default colors
        this.columnColors = this.columns.map((_, index) => {
            if (this.legendData.colors[index]) {
                // Convert decimal to hex if needed
                const color = this.legendData.colors[index];
                return '#' + Number(color).toString(16).padStart(6, '0');
            }
            // Default colors if no legend colors available
            const defaultColors = ['#2196F3', '#FF5722', '#4CAF50', '#FF9800', '#9C27B0', '#607D8B'];
            return defaultColors[index % defaultColors.length];
        });
    }

    /**
     * Get the color for a specific column by index
     */
    getColumnColor(columnIndex: number): string {
        return this.columnColors[columnIndex] || '#2196F3';
    }

    /**
     * Handle column color change from color picker
     */
    onColumnColorChange(columnIndex: number, event: Event) {
        const color = (event.target as HTMLInputElement).value;
        this.columnColors[columnIndex] = color;
    }

    /**
     * Apply changes and close dialog with updated data
     */
    applyChanges() {
        const chartData = this.exportToChartData();
        const updatedLegendColors = this.columnColors.map(color => {
            // Convert hex to decimal for storage
            return parseInt(color.replace('#', ''), 16);
        });
        const updatedLegendTitles = this.columns.map(column => column.label);

        const updatedLegendData = {
            colors: updatedLegendColors,
            titles: updatedLegendTitles
        };

        if (this.dialogRef) {
            this.dialogRef.close({
                chartData,
                legendData: updatedLegendData
            });
        }
    }

    /**
     * Open the import JSON dialog
     */
    openImportDialog() {
        // Populate the textarea with current data or sample data
        this.importJsonText = this.exportToJson();
        this.jsonError = '';

        // Store the import dialog reference so we can close only this dialog
        this.importDialogRef = this.dialog.open(this.importDataDialog, {
            width: '700px',
            maxHeight: '80vh',
            disableClose: false,
            autoFocus: true
        });
    }

    /**
     * Export current grid data to JSON string
     */
    exportToJson(): string {
        if (this.rows.length === 0) {
            // Return sample JSON structure if grid is empty
            return this.getSampleJson();
        }

        const exportData = {
            columns: this.columns.map((col, index) => ({
                id: col.id,
                label: col.label,
                color: this.columnColors[index] || '#2196F3'
            })),
            rows: this.rows.map(row => ({
                label: row.label,
                data: this.columns.map(col => row.data[col.id] || '')
            }))
        };

        return JSON.stringify(exportData, null, 2);
    }

    /**
     * Get sample JSON structure for empty grid
     */
    getSampleJson(): string {
        const sampleData = {
            columns: [
                { id: 'A', label: 'Q1', color: '#2196F3' },
                { id: 'B', label: 'Q2', color: '#FF5722' },
                { id: 'C', label: 'Q3', color: '#4CAF50' },
                { id: 'D', label: 'Q4', color: '#FF9800' }
            ],
            rows: [
                { label: 'Sales', data: ['100', '150', '120', '180'] },
                { label: 'Revenue', data: ['80', '120', '100', '150'] },
                { label: 'Profit', data: ['60', '90', '75', '110'] }
            ]
        };

        return JSON.stringify(sampleData, null, 2);
    }

    /**
     * Apply imported JSON data to the grid
     */
    applyImportedJson() {
        try {
            this.jsonError = '';

            // Parse the JSON
            const importedData = JSON.parse(this.importJsonText);

            // Validate the structure
            if (!importedData.columns || !Array.isArray(importedData.columns)) {
                throw new Error('Invalid JSON: "columns" array is required');
            }

            if (!importedData.rows || !Array.isArray(importedData.rows)) {
                throw new Error('Invalid JSON: "rows" array is required');
            }

            // Clear existing data
            this.columns = [];
            this.rows = [];
            this.columnColors = [];

            // Import columns
            importedData.columns.forEach((col: any, index: number) => {
                const columnId = col.id || String.fromCharCode(65 + index);
                const columnLabel = col.label || columnId;
                const columnColor = col.color || '#2196F3';

                this.columns.push({
                    id: columnId,
                    label: columnLabel,
                    expanded: false
                });

                this.columnColors.push(columnColor);
            });

            // Import rows
            importedData.rows.forEach((row: any, rowIndex: number) => {
                const rowLabel = row.label || `Row ${rowIndex + 1}`;
                const rowData: { [key: string]: string } = {};

                // Map data to columns
                if (Array.isArray(row.data)) {
                    this.columns.forEach((col, colIndex) => {
                        rowData[col.id] = row.data[colIndex] || '';
                    });
                } else {
                    throw new Error('Invalid JSON: row.data must be an array');
                }

                this.rows.push({
                    id: `row_${rowIndex}`,
                    label: rowLabel,
                    expanded: false,
                    data: rowData
                });
            });

            // Close ONLY the import dialog, not the main chart-editor dialog
            if (this.importDialogRef) {
                this.importDialogRef.close();
                this.importDialogRef = undefined;
            }

        } catch (error: any) {
            this.jsonError = error.message || 'Invalid JSON format';
        }
    }

    override destroy() {
        super.destroy();
    }
}
